import React, { useState } from "react";
import styled from "styled-components";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  GridCell as Cell,
  GridHeaderCellProps,
  GridColumnMenuWrapper,
  GridCellProps,
  GRID_COL_INDEX_ATTRIBUTE
} from "@progress/kendo-react-grid";
import { Link } from "react-router-dom";
import moment from "moment";
import { getEnvConfig } from "../../helpers/environment-config";

interface StyledGridHeaderCellProps extends GridHeaderCellProps {
  className?: string;
  children: React.ReactNode;
}

interface StyledGridCellProps extends GridCellProps {
  className?: string;
}

const UnstyledHeaderCell = (props: StyledGridHeaderCellProps) => {
  return (
    <div className={props.className}>
      <div onClick={props.onClick}>
        {props.title}
        {props.children}
      </div>

      <GridColumnMenuWrapper {...props.columnMenuWrapperProps}></GridColumnMenuWrapper>
    </div>
  );
};

// A cell that renders a link to the deal lite page for the given deal.
const DealPageLinkCell = (props: StyledGridCellProps) => {
  const value: string = props.dataItem?.dealName;
  const dealId: number = props.dataItem?.dealId;

  const {
    us438UseDealLitePage: shouldUseDealLitePage,
    us1304UseNewDealPage: shouldUseNewDealPage,
    us1304BrokerFrontendUrlEnvironmentVariable: brokerFrontendURL
  } = useFlags();
  const legacyDealPagePath = shouldUseDealLitePage && props.dataItem.isBrokerCompany ? `/deal-lite/${ dealId }` : `/deal/${ dealId }`;
  const dealPagePath = shouldUseNewDealPage ? `${ brokerFrontendURL }/deals/${ dealId }` : legacyDealPagePath;

  return (
    <td className={props.className} {...{ [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex }}>
      <a href={dealPagePath}>{ value }</a>
    </td>
  );
};

// A normal table cell.
// Automatically renders "--" when the given value is empty.
const TableCell = (props: StyledGridCellProps) => {
  const value: number = props.dataItem[props.field || ""];
  return (
    <td className={props.className} {...{ [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex }}>
      { value || "––" }
    </td>
  );
};

// A cell that displays a date.
// Formats dates as "MM/DD/YYYY" (e.g. 05/01/2025).
// Automatically renders "--" when the given value is empty.
const DateCell = (props: StyledGridCellProps) => {
  const date: Date | null = props.dataItem[props.field || ""];
  const formatted = date ? moment.utc(date).format("MM/DD/yyyy") : null;
  return (
    <td className={props.className} {...{ [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex }}>
      { formatted || "––" }
    </td>
  );
};

// A cell that displays currency.
// Formats currency as USD with magnitude abbreviations (K, MM, B, etc.)
const CurrencyCell = (props: StyledGridCellProps) => {
  const currencyValue: number = props.dataItem[props.field || ""];
  const formatted = currencyValue ? Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1
  }).format(currencyValue) : "––";
  return (
    <td className={props.className} {...{ [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex }}>
      { formatted }
    </td>
  );
};

// A cell that displays a percentage.
// Formats number input as a percentage with two fraction digits and sign.
const PercentageCell = (props: StyledGridCellProps) => {
  const value: number = props.dataItem[props.field || ""];
  const formatted = value ? Intl.NumberFormat(undefined, {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value) : "––";
  return (
    <td className={props.className} {...{ [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex }}>
      { formatted }
    </td>
  );
};

const NumericCell = (props: StyledGridCellProps) => {
  const value: number = props.dataItem[props.field || ""];
  const formatted = value ? value.toLocaleString() : null;
  return (
    <td className={props.className} {...{ [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex }}>
      { formatted ?? "––" }
    </td>
  );
};

const BooleanCell = (props: StyledGridCellProps) => {
  const value: boolean = props.dataItem[props.field || ""];
  const formatted = value ? "Yes" : "No";
  return (
    <td className={props.className} {...{ [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex }}>
      {formatted ?? "––"}
    </td>
  );
};

export const HeaderCell = styled(UnstyledHeaderCell)`
  font-family: 'Lexend';
  font-style: normal;
  font-weight: 500;
  font-size: 10px;
  line-height: 24px;
  text-transform: uppercase;
  color: #84838D;
`;

export const StyledDefaultTableCell = styled(TableCell)`
  font-family: 'Lexend';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  color: #84838D;
`;

export const StyledDealNameCell = styled(DealPageLinkCell)`
  font-family: 'Lexend';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 24px;
  color: #0E3F62;
`;

export const StyledPropertyTypeCell = styled(Cell)`
  font-family: 'Lexend';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 24px;
  color: #0E3F62;
`;

export const StyledCurrencyCell = styled(CurrencyCell)`
  font-family: 'Lexend';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  font-feature-settings: 'zero' on;
  color: #0E3F62;
`;

export const StyledSmallDateCell = styled(DateCell)`
  font-family: 'Lexend';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 24px;
  color: #0E3F62;
`;

export const StyledLargeDateCell = styled(DateCell)`
  font-family: 'Lexend';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 24px;
  color: #0E3F62;
`;

export const StyledRateTypeCell = styled(TableCell)`
  font-family: 'Lexend';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 24px;
  color: #0E3F62;
`;

export const StyledPercentageCell = styled(PercentageCell)`
  font-family: 'IBM Plex Mono';
  font-style: normal;
  font-weight: 300;
  font-size: 14px;
  line-height: 24px;
  font-feature-settings: 'zero' on;
  color: #0E3F62;
`;

export const StyledNumericCell = styled(NumericCell)`
  font-family: 'Lexend';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 24px;
  color: #0E3F62;
`;

export const StyledBooleanCell = styled(BooleanCell)`
  font-family: 'Lexend';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  color: #84838D;
`;
